import { Box, Typography, Stack, Button } from "@mui/material";
import { FolderOutlined, DescriptionOutlined, Download, Upload } from "@mui/icons-material";
import { RECO_SHEETS } from "../data.js";
import { GlobalState } from "context/GlobalContext.js";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance.js";
import { useRef, useState } from "react";
import { AuthState } from "context/AuthContext.js";

export default function CommonFiles() {
	const { user } = AuthState();
	const { setSnackBar, snackBar, setSnackBarOpen, setLoadingOpen } = GlobalState();
	const inputRefs = useRef({}); // <-- FIXED: dynamic refs per sheet
	const [loading, setLoading] = useState(false);

	// -----------------------------
	// Download Handler
	// -----------------------------
	const downloadFiles = async (url, file_type, key) => {
		try {
			setLoadingOpen(true);
			url = `${url}${key}`;

			const response = await pythonVmInstance.get(url, { responseType: "blob" });
			const blob = new Blob([response.data], {
				type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
			});

			const link = document.createElement("a");
			link.href = window.URL.createObjectURL(blob);
			link.download = `${file_type}.csv`;
			link.click();

			setLoadingOpen(false);
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, message: "File Downloaded Successfully", severity: "success" });
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: `Error downloading file: ${error.message}`,
				severity: "error",
			});
			setLoadingOpen(false);
		}
	};

	// -----------------------------
	// File Selection Handler
	// -----------------------------
	const handleFileSelected = async (e, key) => {
		const file = e.target.files[0];
		if (!file) return;

		await handleUploadFile(file, key);

		// Clear input so same file can be selected again
		e.target.value = "";
	};

	// -----------------------------
	// Upload Handler
	// -----------------------------
	const handleUploadFile = async (file, key) => {
		try {
			setLoadingOpen(true);

			const formData = new FormData();
			formData.append("file", file);
			formData.append("key", key);
			formData.append("user", user);

			const response = await pythonVmInstance.post(`/shipping_partner_reco/upload/${key}`, formData, {
				headers: { "Content-Type": "multipart/form-data" },
			});

			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: response.data || "File uploaded successfully!",
				severity: "success",
			});
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: error.response?.data || error.message || "Upload failed",
				severity: "error",
			});
		} finally {
			setLoadingOpen(false);
		}
	};

	// =============================
	// UI Rendering
	// =============================
	return (
		<Box>
			{/* Header */}
			<Box sx={{ mb: 4 }}>
				<Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
					<FolderOutlined sx={{ color: "#757575", fontSize: 28 }} />
					<Typography variant="h6" sx={{ fontWeight: 700, color: "#212121" }}>
						Reference Sheets
					</Typography>
				</Box>
				<Typography variant="body2" sx={{ color: "#757575", fontWeight: 500 }}>
					Common reference files used during reconciliation process
				</Typography>
			</Box>

			{/* Sheets Grid */}
			<Stack spacing={3}>
				{RECO_SHEETS.map((group) => (
					<Box
						key={group.category}
						sx={{
							background: "#fafafa",
							p: 3,
							borderRadius: 2,
							border: "1px solid #e0e0e0",
							transition: "all 0.3s ease",
							"&:hover": {
								transform: "translateY(-2px)",
								boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
							},
						}}>
						<Typography
							variant="h6"
							sx={{
								fontWeight: 600,
								color: "#212121",
								mb: 2,
								display: "flex",
								alignItems: "center",
								gap: 1,
							}}>
							<DescriptionOutlined sx={{ color: "#757575", fontSize: 20 }} />
							{group.category}
						</Typography>

						<Stack spacing={2}>
							{group.sheets.map((sheet) => (
								<Box
									key={sheet.name}
									sx={{
										display: "flex",
										justifyContent: "space-between",
										alignItems: "center",
										p: 2,
										background: "white",
										borderRadius: 2,
										border: "1px solid #e0e0e0",
										transition: "all 0.2s ease",
										"&:hover": {
											boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
											transform: "translateX(4px)",
										},
									}}>
									<Box>
										<Typography variant="body1" sx={{ fontWeight: 600, color: "#212121", mb: 0.5 }}>
											{sheet.name}
										</Typography>
										<Typography variant="caption" sx={{ color: "#757575", fontWeight: 500 }}>
											{sheet.description}
										</Typography>
									</Box>

									<Box sx={{ display: "flex", gap: 1 }}>
										{/* HIDDEN INPUT for this sheet */}
										<input
											type="file"
											ref={(el) => (inputRefs.current[sheet.key] = el)}
											style={{ display: "none" }}
											onChange={(e) => handleFileSelected(e, sheet.key)}
											accept=".csv,.xlsx"
										/>

										{/* UPLOAD BUTTON */}
										<Button
											onClick={() => inputRefs.current[sheet.key].click()}
											rel="noopener noreferrer"
											underline="none"
											disabled={loading}
											sx={{
												transition: "all 0.2s ease",
												"&:hover": {
													background: "#424242",
													transform: "scale(1.05)",
												},
											}}
											variant="contained">
											Upload
											<Upload sx={{ fontSize: 16 }} />
										</Button>

										{/* DOWNLOAD BUTTON */}
										<Button
											onClick={() => downloadFiles(sheet.url, sheet.name, sheet.key)}
											rel="noopener noreferrer"
											underline="none"
											sx={{
												transition: "all 0.2s ease",
												"&:hover": {
													background: "#424242",
													transform: "scale(1.05)",
												},
											}}
											variant="contained">
											Download
											<Download sx={{ fontSize: 16 }} />
										</Button>
									</Box>
								</Box>
							))}
						</Stack>
					</Box>
				))}
			</Stack>
		</Box>
	);
}
